import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Box, Button, Typography, useTheme } from '@mui/material';

import { gql } from '@graphql/__generated__';
import {
  type ActivityNextStepAction,
  ActivityNextStepsDialog,
} from '@modules/_shared/components/feed/ActivityNextStepsDialog';
import { createActivityNextStepOptions } from '@modules/_shared/components/feed/activityOutcomeShared';
import type {
  ActivityFollowUpContext,
  OutcomeFollowUpAction,
} from '@modules/_shared/components/feed/activityOutcomeShared';
import type { PipelineLeadType } from '@modules/_shared/components/feed/types';
import { GET_PENDING_ACTIVITIES } from '@modules/_shared/components/feed/utils/activityQueries';
import { createNextStepsCloseHandler } from '@modules/_shared/components/feed/utils/outcomeDialogHelpers';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import type { PendingPlanActivity } from '@modules/lead-details/hooks/usePlanActivityConflict';
import { useLocale } from '@shared/hooks/locale';

type TaskOutcomeDialogFlowProps = {
  open: boolean;
  activity: PendingPlanActivity | null;
  onClose: () => void;
  onFollowUpActionSelect?: (action: OutcomeFollowUpAction) => void;
  canMarkLeadLost?: boolean;
  autoOutcome?: 'completed' | 'cancelled' | null;
  parentType?: null | 'Enquiry' | 'Lot' | 'Lead' | 'User' | 'BuyerLead';
  activityId?: string | null;
  pipelineType: PipelineLeadType;
  onLoadingChange?: (
    loading: boolean,
    outcomeType: 'completed' | 'cancelled' | null,
  ) => void;
};

const COMPLETE_TASK = gql(/* GraphQL */ `
  mutation TaskOutcomeDialogFlowCompleteTask(
    $id: uuid!
    $success: Boolean!
    $note: String
  ) {
    update_activities_by_pk(
      pk_columns: { id: $id }
      _set: { done: true, success: $success, done_at: "now()", note: $note }
    ) {
      id
    }
  }
`);

type HandleCancelOptions = {
  preserveAutoHandled?: boolean;
};

export const TaskOutcomeDialogFlow = ({
  open,
  activity,
  activityId = null,
  onClose,
  onFollowUpActionSelect,
  canMarkLeadLost = false,
  autoOutcome = null,
  parentType,
  pipelineType,
  onLoadingChange,
}: TaskOutcomeDialogFlowProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const [errorLike, setErrorLike] = useState<ErrorLike | null>(null);

  const effectiveActivityId = activity?.id ?? activityId ?? null;
  const activityNote = activity?.note ?? null;

  const [nextStepsContext, setNextStepsContext] =
    useState<ActivityFollowUpContext<PendingPlanActivity | null> | null>(null);
  const [autoHandled, setAutoHandled] = useState(false);
  const selectedActionRef = useRef<OutcomeFollowUpAction | null>(null);
  const skipOutcomeDialog =
    autoOutcome === 'cancelled' || autoOutcome === 'completed';

  const isBrokeragePipeline = pipelineType === 'sales';

  const handleCancel = useCallback(
    ({ preserveAutoHandled = false }: HandleCancelOptions = {}) => {
      if (!preserveAutoHandled) {
        setAutoHandled(false);
      }
      onClose();
    },
    [onClose],
  );

  const [completeTask, { loading }] = useMutation(COMPLETE_TASK, {
    refetchQueries: [GET_PENDING_ACTIVITIES],
    onError: setErrorLike,
    awaitRefetchQueries: true,
  });

  const currentOutcomeRef = useRef<'completed' | 'cancelled' | null>(null);

  useEffect(() => {
    if (loading && currentOutcomeRef.current) {
      onLoadingChange?.(loading, currentOutcomeRef.current);
    } else if (!loading) {
      onLoadingChange?.(false, null);
      currentOutcomeRef.current = null;
    }
  }, [loading, onLoadingChange]);

  useEffect(() => {
    if (autoOutcome) {
      currentOutcomeRef.current = autoOutcome;
    }
  }, [autoOutcome]);

  useEffect(() => {
    if (open) {
      setAutoHandled(false);
    }

    if (!open) {
      setAutoHandled(false);
    }
  }, [open]);

  const submitOutcome = useCallback(
    async ({
      success,
      onCompleted,
    }: {
      success: boolean;
      onCompleted?: () => void;
    }) => {
      if (!effectiveActivityId) {
        onClose();
        return;
      }

      await completeTask({
        variables: {
          id: effectiveActivityId,
          success,
          note: activityNote,
        },
      });

      emitFeedRefresh();

      onCompleted?.();
    },
    [completeTask, effectiveActivityId, activityNote, onClose],
  );

  const handleNotCompleted = useCallback(async () => {
    if (!effectiveActivityId) {
      onClose();
      return;
    }

    currentOutcomeRef.current = 'cancelled';
    const followUpActivity = activity ?? {
      id: effectiveActivityId,
      note: activityNote ?? undefined,
    };

    await submitOutcome({
      success: false,
      onCompleted: () => {
        setNextStepsContext({
          task: followUpActivity,
          success: false,
          isBrokeragePipeline,
        });
      },
    });
  }, [
    activity,
    activityNote,
    effectiveActivityId,
    submitOutcome,
    onClose,
    isBrokeragePipeline,
  ]);

  const handleCompleted = useCallback(async () => {
    if (!effectiveActivityId) {
      onClose();
      return;
    }

    currentOutcomeRef.current = 'completed';
    const followUpActivity = activity ?? {
      id: effectiveActivityId,
      note: activityNote ?? undefined,
    };

    await submitOutcome({
      success: true,
      onCompleted: () => {
        setNextStepsContext({
          task: followUpActivity,
          success: true,
          isBrokeragePipeline,
        });
      },
    });
  }, [
    activity,
    activityNote,
    effectiveActivityId,
    submitOutcome,
    onClose,
    isBrokeragePipeline,
  ]);

  const handleCloseNextSteps = useCallback(() => {
    setNextStepsContext(null);
    handleCancel({ preserveAutoHandled: autoHandled });
  }, [autoHandled, handleCancel]);

  const handleNextStepSelect = (action: ActivityNextStepAction) => {
    if (!nextStepsContext) {
      return;
    }

    // Type guard to ensure action is valid for task outcomes
    const validActions: ActivityNextStepAction[] = [
      'schedule_visit',
      'schedule_call',
      'schedule_task',
      'lead_lost',
      'lead_won',
      'note',
      'do_nothing',
    ];

    if (!validActions.includes(action)) {
      return;
    }

    selectedActionRef.current = action as OutcomeFollowUpAction;

    onFollowUpActionSelect?.(action as OutcomeFollowUpAction);

    setNextStepsContext(null);

    handleCloseNextSteps();
  };

  const followUpSuccess = nextStepsContext?.success ?? false;
  const followUpBrokerage = nextStepsContext?.isBrokeragePipeline ?? false;

  const palette = theme.palette;
  const nextStepOptions = useMemo(() => {
    return createActivityNextStepOptions({
      t,
      palette,
      canMarkLeadLost,
      followUpSuccess,
      followUpBrokerage,
      parentType,
    });
  }, [
    canMarkLeadLost,
    followUpBrokerage,
    followUpSuccess,
    palette,
    t,
    parentType,
  ]);

  useEffect(() => {
    if (!open || autoHandled) {
      return;
    }

    if (autoOutcome === 'completed') {
      setAutoHandled(true);
      void handleCompleted();
      return;
    }

    if (autoOutcome === 'cancelled') {
      setAutoHandled(true);
      void handleNotCompleted();
    }
  }, [autoOutcome, handleCompleted, handleNotCompleted, open, autoHandled]);

  const outcomeDialogOpen =
    open && !skipOutcomeDialog && nextStepsContext == null;
  const nextStepsDialogOpen = Boolean(nextStepsContext);

  return (
    <>
      <SlideDialog
        open={outcomeDialogOpen}
        onClose={() => handleCancel()}
        title={t('Did you complete the task?')}
        disableCancel
        customPrimaryActions={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            <Button
              color="error"
              variant="contained"
              onClick={handleNotCompleted}
              loading={loading && currentOutcomeRef.current === 'cancelled'}
              loadingPosition="start"
            >
              {t('Cancel')}
            </Button>
            <Button
              color="success"
              variant="contained"
              onClick={handleCompleted}
              loading={loading && currentOutcomeRef.current === 'completed'}
              loadingPosition="start"
            >
              {t('Completed')}
            </Button>
          </Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">
            {t('Indicate if you completed the task.')}
          </Typography>
        </Box>
      </SlideDialog>

      <MutationErrorSnackbar
        error={errorLike}
        onClose={() => setErrorLike(null)}
      />

      <ActivityNextStepsDialog
        open={nextStepsDialogOpen}
        onClose={createNextStepsCloseHandler(
          selectedActionRef,
          handleCloseNextSteps,
        )}
        title={followUpSuccess ? t('Task completed') : t('Task not completed')}
        options={nextStepOptions}
        onSelect={handleNextStepSelect}
      />
    </>
  );
};
